import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Button, Card } from 'sa-ui';
import uniq from 'lodash/uniq';
import * as xlsxUtil from 'app/utils/xlsx';
import messages from './gradePerQuestionTableMessages';

import './GradePerQuestionTable.scss';

function getQuestionsNumber(data) {
  const subtotal = data.map((d) => d.answers.length);
  const maxQuestions = Math.max(...subtotal);
  return Array(maxQuestions)
    .fill()
    .map((_, index) => index + 1);
}
function getDistinctDisciplines(data) {
  const disciplines = data
    .map((d) => d.curricularComponents.map((cc) => cc.abbreviation))
    .reduce((a1, a2) => a1.concat(a2), []);
  return uniq(disciplines);
}

function toXLSXData(data, intl) {
  const firstCols = [
    { name: intl.formatMessage(messages.class) },
    { name: intl.formatMessage(messages.name) },
    { name: intl.formatMessage(messages.number) },
    { name: intl.formatMessage(messages.timeStart) },
    { name: intl.formatMessage(messages.timeFinish) },
    { name: intl.formatMessage(messages.timeTaken) },
  ];
  const questionsNumbers = getQuestionsNumber(data);
  const disciplinesAbbreviation = getDistinctDisciplines(data);
  const questionsCols = questionsNumbers.map((_, index) => ({ name: `Q${index + 1}` }));
  const disciplineCols = disciplinesAbbreviation
    .map((abbreviation) => [{ name: abbreviation || '-' }, { name: '%' }])
    .reduce((a1, a2) => a1.concat(a2), []);
  const endCols = [{ name: intl.formatMessage(messages.hits) }, { name: '%' }];
  const cols = firstCols
    .concat(questionsCols)
    .concat(disciplineCols)
    .concat(endCols)
    .map((c, index) => ({ ...c, key: index }));
  const dataRows = data.map((d) => {
    const firstDataCols = [
      d.courseName,
      d.userName,
      d.registration,
      d.assessmentTimeStart || '-',
      d.assessmentTimeFinish || '-',
      d.assessmentTimeTaken || '-',
    ];
    const questionDataCols = questionsNumbers.map((_, index) =>
      d.answers[index] ? d.answers[index].correct : '-',
    );
    const disciplineDataCols = disciplinesAbbreviation
      .map((abbreviation) => {
        const discipline =
          d.curricularComponents.find((cc) => cc.abbreviation === abbreviation) || {};
        return [
          discipline.totalHits || '-',
          discipline.hitsPercentage ? `${discipline.hitsPercentage}%` : '-',
        ];
      })
      .reduce((a1, a2) => a1.concat(a2), []);
    const endDataCols = [d.hits, `${d.hitsPercentage}%`];
    return firstDataCols.concat(questionDataCols).concat(disciplineDataCols).concat(endDataCols);
  });
  const firstRow = [cols.map((c) => c.name)];
  return {
    cols,
    data: firstRow.concat(dataRows),
  };
}

const GradePerQuestionTable = (props) => {
  function exportXLSX() {
    const xlsxData = toXLSXData(props.data, props.intl);
    xlsxUtil.exportXLSX(
      xlsxData.data,
      props.intl.formatMessage(messages.title),
      `${props.intl.formatMessage(messages.filename)}.xlsx`,
    );
  }

  const questionsNumber = getQuestionsNumber(props.data);
  const disciplinesAbbreviation = getDistinctDisciplines(props.data);

  function getQuestionLabel(index) {
    if (!props.data && !props.data.length) return '';
    const question = props.data[0].answers[index];
    if (!question) return '';
    const isOptional = question.idQuestion === question.idQuestionOptional;
    return `Q${isOptional ? 'OPT' : ''}${question.position}`;
  }

  return (
    <Card className="grade-per-question-table">
      <div className="grade-header">
        <h5 className="title mt-0">
          <i className="ti ti-stats-up mr-2" />
          {props.intl.formatMessage(messages.title)}
        </h5>
        <Button onClick={exportXLSX}>{props.intl.formatMessage(messages.export)}</Button>
      </div>
      <div className="table-container">
        <table className="default-table">
          <thead>
            <tr>
              <th>{props.intl.formatMessage(messages.class)}</th>
              <th>{props.intl.formatMessage(messages.name)}</th>
              <th>{props.intl.formatMessage(messages.number)}</th>
              <th>{props.intl.formatMessage(messages.timeStart)}</th>
              <th>{props.intl.formatMessage(messages.timeFinish)}</th>
              <th>{props.intl.formatMessage(messages.timeTaken)}</th>
              {questionsNumber.map((_, index) => (
                <th>{getQuestionLabel(index)}</th>
              ))}
              {disciplinesAbbreviation.map((abbreviation) => (
                <div>
                  <th>{abbreviation || '-'}</th>
                  <th>%</th>
                </div>
              ))}
              <th>{props.intl.formatMessage(messages.hits)}</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {props.data.map((d) => (
              <tr>
                <td>{d.courseName}</td>
                <td>{d.userName}</td>
                <td>{d.registration}</td>
                <td>{d.assessmentTimeStart || '-'}</td>
                <td>{d.assessmentTimeFinish || '-'}</td>
                <td>{d.assessmentTimeTaken || '-'}</td>
                {questionsNumber.map((_, index) => (
                  <td>{d.answers[index] ? d.answers[index].correct : '-'}</td>
                ))}
                {disciplinesAbbreviation.map((abbreviation) => {
                  const discipline =
                    d.curricularComponents.find((cc) => cc.abbreviation === abbreviation) || {};
                  return (
                    <div>
                      <td>{discipline.totalHits || '-'}</td>
                      <td>{discipline.hitsPercentage ? `${discipline.hitsPercentage}%` : '-'}</td>
                    </div>
                  );
                })}
                <td>{d.hits}</td>
                <td>{d.hitsPercentage}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

GradePerQuestionTable.propTypes = {};

GradePerQuestionTable.defaultProps = {
  data: [],
};

export default injectIntl(GradePerQuestionTable);
